import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Building2, Briefcase, User, X, Search, Handshake } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";

interface SaveMessageDialogProps {
	isOpen: boolean;
	onClose: () => void;
	messageData: {
		id?: string; // Message ID for duplicate prevention
		text: string;
		subject?: string;
		messageType?: string;
		participants?: any;
	};
	currentEntity?: {
		type: 'candidates' | 'jobs' | 'companies';
		id: string;
		name?: string;
	};
	onMessageSaved?: () => void;
	// Edit mode props
	editMode?: boolean;
	existingSavedMessageId?: string;
	existingRelations?: {
		candidates: EntityItem[];
		jobs: EntityItem[];
		companies: EntityItem[];
	};
}

interface EntityItem {
	id: string;
	name: string;
	title?: string;
	secondary?: string;
	type: 'candidates' | 'jobs' | 'companies';
}

interface SelectedRelations {
	candidates: string[];
	jobs: string[];
	companies: string[];
}

export const SaveMessageDialog: React.FC<SaveMessageDialogProps> = ({
	isOpen,
	onClose,
	messageData,
	currentEntity,
	onMessageSaved,
	editMode = false,
	existingSavedMessageId,
	existingRelations
}) => {
	const [subject, setSubject] = useState(messageData.subject || "");
	const [selectedRelations, setSelectedRelations] = useState<SelectedRelations>({
		candidates: [],
		jobs: [],
		companies: []
	});
	const [selectedItems, setSelectedItems] = useState<{
		candidates: EntityItem[];
		jobs: EntityItem[];
		companies: EntityItem[];
	}>({
		candidates: [],
		jobs: [],
		companies: []
	});
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<EntityItem[]>([]);
	const [activeTab, setActiveTab] = useState<'candidates' | 'jobs' | 'companies'>('candidates');
	const [isSearching, setIsSearching] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [loading, setLoading] = useState<{[key: string]: boolean}>({
		candidates: false,
		jobs: false,
		companies: false
	});
	const { currentOrganization, user } = useAuth();

	// Initialize with existing relations in edit mode, or current entity if provided
	useEffect(() => {
		if (editMode && existingRelations) {
			// Initialize with existing relations for edit mode
			setSelectedRelations({
				candidates: existingRelations.candidates.map(c => c.id),
				jobs: existingRelations.jobs.map(j => j.id),
				companies: existingRelations.companies.map(c => c.id)
			});
			setSelectedItems(existingRelations);
		} else if (currentEntity && currentEntity.id) {
			// Initialize with current entity if provided (new save mode)
			setSelectedRelations(prev => ({
				...prev,
				[currentEntity.type]: [currentEntity.id]
			}));
			
			if (currentEntity.name) {
				setSelectedItems(prev => ({
					...prev,
					[currentEntity.type]: [{
						id: currentEntity.id,
						name: currentEntity.name,
						type: currentEntity.type
					}]
				}));
			} else {
				// Load entity data if name not provided
				loadEntityData(currentEntity.type, currentEntity.id);
			}
		}
	}, [currentEntity, editMode, existingRelations]);

	const loadEntityData = async (entityType: string, entityId: string) => {
		if (!currentOrganization?.id) return;

		try {
			let query;
			let entityData: EntityItem | null = null;

			switch (entityType) {
				case 'candidates':
					const { data: candidateData } = await supabase
						.from('candidates')
						.select('id, name, email')
						.eq('organization_uuid', currentOrganization.id)
						.eq('id', entityId)
						.single();
					
					if (candidateData) {
						entityData = {
							id: candidateData.id,
							name: candidateData.name,
							secondary: candidateData.email || undefined,
							type: 'candidates'
						};
					}
					break;

				case 'jobs':
					const { data: jobData } = await supabase
						.from('jobs')
						.select('id, title, company_id, company:company_id (name)')
						.eq('organization_uuid', currentOrganization.id)
						.eq('id', entityId)
						.single();
					
					if (jobData) {
						entityData = {
							id: jobData.id,
							name: jobData.title,
							secondary: jobData.company?.name || undefined,
							type: 'jobs'
						};
					}
					break;

				case 'companies':
					const { data: companyData } = await supabase
						.from('companies')
						.select('id, name')
						.eq('organization_uuid', currentOrganization.id)
						.eq('id', entityId)
						.single();
					
					if (companyData) {
						entityData = {
							id: companyData.id,
							name: companyData.name,
							type: 'companies'
						};
					}
					break;
			}

			if (entityData) {
				setSelectedItems(prev => ({
					...prev,
					[entityType]: [entityData]
				}));
			}
		} catch (error) {
			console.error('Error loading entity data:', error);
		}
	};

	// Search entities
	useEffect(() => {
		if (searchQuery.trim()) {
			searchEntities();
		} else {
			setSearchResults([]);
		}
	}, [searchQuery, activeTab]);

	const searchEntities = async () => {
		if (!searchQuery.trim() || !currentOrganization?.id) return;

		setIsSearching(true);
		try {
			let data: EntityItem[] = [];

			switch (activeTab) {
				case 'candidates':
					const { data: candidatesData } = await supabase
						.from('candidates')
						.select('id, name, email')
						.eq('organization_uuid', currentOrganization.id)
						.ilike('name', `%${searchQuery}%`)
						.limit(20);
					
					data = candidatesData?.map(c => ({
						id: c.id,
						name: c.name,
						secondary: c.email || undefined,
						type: 'candidates' as const
					})) || [];
					break;

				case 'jobs':
					const { data: jobsData } = await supabase
						.from('jobs')
						.select('id, title, company_id, company:company_id (name)')
						.eq('organization_uuid', currentOrganization.id)
						.ilike('title', `%${searchQuery}%`)
						.limit(20);
					
					data = jobsData?.map(job => ({
						id: job.id,
						name: job.title,
						secondary: job.company?.name || undefined,
						type: 'jobs' as const
					})) || [];
					break;

				case 'companies':
					const { data: companiesData } = await supabase
						.from('companies')
						.select('id, name')
						.eq('organization_uuid', currentOrganization.id)
						.ilike('name', `%${searchQuery}%`)
						.limit(20);
					
					data = companiesData?.map(c => ({
						id: c.id,
						name: c.name,
						type: 'companies' as const
					})) || [];
					break;
			}

			setSearchResults(data);
		} catch (err) {
			console.error('Error searching entities:', err);
		} finally {
			setIsSearching(false);
		}
	};

	// Add entity to selected entities
	const handleEntitySelect = (entity: EntityItem) => {
		const entityType = entity.type;
		
		// Check if already selected
		if (selectedRelations[entityType].includes(entity.id)) return;

		// Add to selected relations
		setSelectedRelations(prev => ({
			...prev,
			[entityType]: [...prev[entityType], entity.id]
		}));

		// Add to selected items
		setSelectedItems(prev => ({
			...prev,
			[entityType]: [...prev[entityType], entity]
		}));

		// Clear search
		setSearchQuery("");
		setSearchResults([]);
	};

	// Remove entity from selected entities
	const handleEntityRemove = (entityType: string, entityId: string) => {
		setSelectedRelations(prev => ({
			...prev,
			[entityType]: prev[entityType as keyof SelectedRelations].filter(id => id !== entityId)
		}));

		setSelectedItems(prev => ({
			...prev,
			[entityType]: prev[entityType as keyof typeof prev].filter(item => item.id !== entityId)
		}));
	};

	// Filter out already selected items from search results
	const getFilteredResults = () => {
		const selectedIds = selectedRelations[activeTab];
		return searchResults.filter(item => !selectedIds.includes(item.id));
	};

	// Save the message
	const handleSave = async () => {
		if (!messageData.text.trim() || !currentOrganization?.id) {
			toast({
				title: "Error",
				description: "Missing message text or organization",
				variant: "destructive",
			});
			return;
		}

		if (!hasSelectedEntities()) {
			toast({
				title: "Error",
				description: "Please select at least one entity to link",
				variant: "destructive",
			});
			return;
		}

		if (!editMode && !messageData.id) {
			toast({
				title: "Error",
				description: "Message ID is required to prevent duplicates",
				variant: "destructive",
			});
			return;
		}

		if (editMode && !existingSavedMessageId) {
			toast({
				title: "Error",
				description: "Saved message ID is required for updates",
				variant: "destructive",
			});
			return;
		}

		setIsSaving(true);
		try {
			let savedMessage;

			if (editMode && existingSavedMessageId) {
				// Edit mode: Update existing message subject
				const { data: updatedMessage, error: updateError } = await supabase
					.from('public_messages')
					.update({
						subject: subject || null
					})
					.eq('id', existingSavedMessageId)
					.select()
					.single();

				if (updateError) {
					console.error('Error updating message:', updateError);
					toast({
						title: "Error",
						description: `Failed to update message: ${updateError.message}`,
						variant: "destructive",
					});
					return;
				}

				savedMessage = updatedMessage;

				// Delete existing relations
				const deletePromises = [
					supabase.from('public_message_candidate').delete().eq('public_message_id', existingSavedMessageId),
					supabase.from('public_message_jobs').delete().eq('public_message_id', existingSavedMessageId),
					supabase.from('public_message_company').delete().eq('public_message_id', existingSavedMessageId)
				];
				await Promise.all(deletePromises);
			} else {
				// Check if message already exists (new save mode)
				const { data: existingMessage, error: checkError } = await supabase
					.from('public_messages')
					.select('id')
					.eq('original_message_key', messageData.id)
					.eq('organization_uuid', currentOrganization.id)
					.single();

				if (checkError && checkError.code !== 'PGRST116') {
					console.error('Error checking for existing message:', checkError);
					toast({
						title: "Error",
						description: `Failed to check for duplicate message: ${checkError.message}`,
						variant: "destructive",
					});
					return;
				}

				if (existingMessage) {
					toast({
						title: "Already Saved",
						description: "This message has already been saved to your organization",
						variant: "destructive",
					});
					return;
				}

				// Insert the main message
				const { data: newMessage, error: messageError } = await supabase
					.from('public_messages')
					.insert({
						original_message_key: messageData.id,
						message_type: messageData.messageType,
						subject: subject || null,
						message: messageData.text.trim(),
						organization_uuid: currentOrganization.id,
						participants: messageData.participants || null,
						include_in_ai_analysis: false
					})
					.select()
					.single();

				if (messageError) {
					console.error('Error saving message:', messageError);
					toast({
						title: "Error",
						description: `Failed to save message: ${messageError.message}`,
						variant: "destructive",
					});
					return;
				}

				savedMessage = newMessage;
			}

			console.log('Message saved/updated successfully:', savedMessage);

			// Link entities
			const linkPromises: Promise<any>[] = [];

			// Link candidates
			selectedRelations.candidates.forEach(candidateId => {
				linkPromises.push(
					supabase
						.from('public_message_candidate')
						.insert({
							public_message_id: savedMessage.id,
							candidate_id: candidateId
						})
						.select()
				);
			});

			// Link jobs
			selectedRelations.jobs.forEach(jobId => {
				linkPromises.push(
					supabase
						.from('public_message_jobs')
						.insert({
							public_message_id: savedMessage.id,
							job_id: jobId
						})
						.select()
				);
			});

			// Link companies
			selectedRelations.companies.forEach(companyId => {
				linkPromises.push(
					supabase
						.from('public_message_company')
						.insert({
							public_message_id: savedMessage.id,
							company_id: companyId
						})
						.select()
				);
			});

			await Promise.all(linkPromises);
			console.log('All entity links saved successfully');

			toast({
				title: "Success",
				description: editMode ? "Message relations updated successfully" : "Message saved successfully",
			});

			onMessageSaved?.();
			onClose();
		} catch (err) {
			console.error('Error saving message:', err);
			toast({
				title: "Error",
				description: `Failed to save message: ${err instanceof Error ? err.message : 'Unknown error'}`,
				variant: "destructive",
			});
		} finally {
			setIsSaving(false);
		}
	};

	// Reset form when dialog closes
	const handleClose = () => {
		setSubject(messageData.subject || "");
		setSelectedRelations({
			candidates: currentEntity?.type === 'candidates' && currentEntity?.id ? [currentEntity.id] : [],
			jobs: currentEntity?.type === 'jobs' && currentEntity?.id ? [currentEntity.id] : [],
			companies: currentEntity?.type === 'companies' && currentEntity?.id ? [currentEntity.id] : [],
		});
		setSelectedItems({
			candidates: currentEntity?.type === 'candidates' && currentEntity?.id && currentEntity?.name ? 
				[{ id: currentEntity.id, name: currentEntity.name, type: 'candidates' }] : [],
			jobs: currentEntity?.type === 'jobs' && currentEntity?.id && currentEntity?.name ? 
				[{ id: currentEntity.id, name: currentEntity.name, type: 'jobs' }] : [],
			companies: currentEntity?.type === 'companies' && currentEntity?.id && currentEntity?.name ? 
				[{ id: currentEntity.id, name: currentEntity.name, type: 'companies' }] : [],
		});
		setSearchQuery("");
		setSearchResults([]);
		setActiveTab('candidates');
		onClose();
	};

	// Check if at least one entity is selected
	const hasSelectedEntities = () => {
		return selectedRelations.candidates.length > 0 || 
			   selectedRelations.jobs.length > 0 || 
			   selectedRelations.companies.length > 0;
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-[98vw] max-h-[98vh] w-[80vw] h-[98vh] overflow-hidden p-6">
				<DialogHeader>
					<DialogTitle>{editMode ? "Update Message Relations" : "Save Message"}</DialogTitle>
				</DialogHeader>

				<div className="flex gap-6 h-[calc(98vh-120px)]">
					{/* Left side - Message content */}
					<div className="flex-1 flex flex-col gap-4">
						{/* Subject */}
						<div>
							<Label htmlFor="subject">Subject (optional)</Label>
							<Input
								id="subject"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								placeholder="Enter a subject for this message..."
							/>
						</div>

						{/* Message content - real iframe */}
						<div className="flex-1 min-h-0">
							<Label>Message Content</Label>
							<div className="mt-2 border rounded-sm bg-gray-50 h-full overflow-hidden">
								{messageData.text.includes('<') ? (
									<iframe
										sandbox="allow-same-origin"
										style={{ 
											width: "100%", 
											height: "100%",
											minHeight: "300px",
											border: "none",
											borderRadius: "6px"
										}}
										srcDoc={messageData.text}
										onLoad={(e) => {
											try {
												const iframe = e.target as HTMLIFrameElement;
												const doc = iframe.contentDocument || iframe.contentWindow?.document;
												if (doc && doc.body) {
													const contentHeight = Math.max(
														doc.body.scrollHeight,
														doc.body.offsetHeight,
														doc.documentElement?.scrollHeight || 0
													);
													const adjustedHeight = Math.min(Math.max(contentHeight + 20, 300), 600);
													iframe.style.height = `${adjustedHeight}px`;
												}
											} catch (error) {
												// Fallback to default height if there's a cross-origin issue
											}
										}}
									/>
								) : (
									<div className="p-4 h-full overflow-y-auto">
										<div className="whitespace-pre-wrap text-sm text-gray-800">
											{messageData.text}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Right side - Link to entities */}
					<div className="w-96 flex flex-col">
						<div className="flex items-center gap-2 mb-2">
							<Label>Link to Entities</Label>
							<span className="text-xs text-red-600">*Required</span>
						</div>
						<div className="flex-1 border rounded-sm flex flex-col">
							<Tabs value={activeTab} onValueChange={(value: 'candidates' | 'jobs' | 'companies') => setActiveTab(value)} className="flex-1 flex flex-col">
								<TabsList className="w-full grid grid-cols-3 m-2">
									<TabsTrigger value="candidates" className="flex items-center gap-2">
										<User className="h-4 w-4" />
										Candidates
									</TabsTrigger>
									<TabsTrigger value="jobs" className="flex items-center gap-2">
										<Briefcase className="h-4 w-4" />
										Jobs
									</TabsTrigger>
									<TabsTrigger value="companies" className="flex items-center gap-2">
										<Building2 className="h-4 w-4" />
										Companies
									</TabsTrigger>
								</TabsList>

								{/* Selected items section */}
								{selectedRelations[activeTab].length > 0 && (
									<div className="mx-2 mb-2 border rounded-sm p-2 bg-gray-50 max-h-[120px] overflow-y-auto">
										<h4 className="text-sm font-medium mb-2 text-gray-500">Selected {activeTab}</h4>
										<div className="flex flex-wrap gap-1">
											{selectedItems[activeTab].map((item) => (
												<Badge key={item.id} variant="outline" className="flex items-center gap-1">
													{activeTab === 'candidates' && <User className="h-3 w-3" />}
													{activeTab === 'jobs' && <Briefcase className="h-3 w-3" />}
													{activeTab === 'companies' && <Building2 className="h-3 w-3" />}
													<span className="text-xs">{item.name}</span>
													<button
														onClick={() => handleEntityRemove(activeTab, item.id)}
														className="ml-1 hover:text-red-600"
													>
														<X className="h-3 w-3" />
													</button>
												</Badge>
											))}
										</div>
									</div>
								)}

								{/* Search input */}
								<div className="relative mx-2 mb-2">
									<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
									<Input
										placeholder={`Search ${activeTab}...`}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										className="pl-8"
									/>
								</div>

								{/* Search results */}
								<ScrollArea className="flex-1 mx-2 mb-2">
									<div className="space-y-1">
										{isSearching ? (
											<div className="text-center py-4 text-sm text-gray-500">
												Searching...
											</div>
										) : searchQuery && getFilteredResults().length === 0 ? (
											<div className="text-center py-4 text-sm text-gray-500">
												No {activeTab} found
											</div>
										) : (
											getFilteredResults().map((item) => (
												<div
													key={item.id}
													className="flex items-center justify-between p-2 rounded-sm hover:bg-gray-100 cursor-pointer"
													onClick={() => handleEntitySelect(item)}
												>
													<div className="flex items-center gap-2">
														{activeTab === 'candidates' && <User className="h-4 w-4" />}
														{activeTab === 'jobs' && <Briefcase className="h-4 w-4" />}
														{activeTab === 'companies' && <Building2 className="h-4 w-4" />}
														<div>
															<div className="text-sm font-medium">{item.name}</div>
															{item.secondary && (
																<div className="text-xs text-gray-500">{item.secondary}</div>
															)}
														</div>
													</div>
												</div>
											))
										)}
									</div>
								</ScrollArea>
							</Tabs>
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						Cancel
					</Button>
					<Button 
						onClick={handleSave} 
						disabled={isSaving || !messageData.text.trim() || !hasSelectedEntities()}
					>
						{isSaving ? (editMode ? "Updating..." : "Saving...") : (editMode ? "Update Relations" : "Save Message")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
